import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { CARD_EYEBROW } from "../../primitives/type";
import type { OpacityMix } from "./types";

/**
 * The bordered panel the card's content slot renders into, and the dashed row
 * divider inside it. Extracted because the signals list, the plan mosaic and
 * the reflection look-back had each grown their own copy and the three headers
 * that should look identical had already drifted apart on spacing, tracking and
 * element.
 */
export function AttentionPanel({
  aside,
  busy = false,
  children,
  className,
  label,
  opacityMix,
}: {
  /** Right-hand meta on the header row, e.g. "Cycle 3". */
  aside?: ReactNode;
  busy?: boolean;
  children: ReactNode;
  className?: string;
  label: string;
  opacityMix: OpacityMix;
}) {
  return (
    <section
      className={cn("relative z-10 mt-4 rounded-lg border px-4 py-5", className)}
      style={{ borderColor: "var(--attn-panel-border)", background: "var(--attn-panel-bg)" }}
      aria-label={label}
      aria-busy={busy || undefined}
    >
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <span className={cn(PANEL_LABEL)} style={{ color: opacityMix(70) }}>
          {label}
        </span>
        {aside ? (
          <span className={cn(PANEL_LABEL, "tabular-nums")} style={{ color: opacityMix(70) }}>
            {aside}
          </span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export const PANEL_LABEL = CARD_EYEBROW;

/** A row in a panel list, hairline-separated from the next one. */
export const PANEL_ROW = "border-b border-dashed pb-3 last:border-b-0 last:pb-0";

export const panelRowStyle = { borderColor: "var(--attn-divider)" };
